import type { WidgetTabId } from '../../widgetConfig';
import type { TimetableDetailsLine } from '../../data/timetableDetailsLine';
import {
  canGoBack,
  createInitialPageStackState,
  getCurrentPage,
  getPageStack,
  reducePageStackState,
  type PageStackAction,
  type PageStackState,
} from '../base/navigation/pageStack';

/**
 * JourneyWidgetTabs-specific page state adapter.
 *
 * This module binds the generic page-stack core to the widget's concrete
 * activities and page kinds.
 */

export type JourneyPageKind = 'search' | 'results' | 'details';

export type JourneyPlannerDetailsPage =
  | { type: 'itinerary'; itineraryId: number }
  | {
      type: 'stop-departures';
      itineraryId: number;
      stopAtcoCode: string;
      stopName?: string | null;
    }
  | {
      type: 'line-timetable';
      itineraryId: number;
      lineId: string;
      initialLine?: TimetableDetailsLine | null;
    };

export interface PlanStopDeparturesDetailsPageOptions {
  itineraryId: number;
  stopAtcoCode: string;
  stopName?: string | null;
}

export interface PlanLineTimetableDetailsPageOptions {
  itineraryId: number;
  lineId: string;
  initialLine?: TimetableDetailsLine | null;
}

export interface TimetableStopDeparturesDetailsPageOptions {
  lineId: string;
  stopAtcoCode: string;
  stopName?: string | null;
}

export type JourneyWidgetDetailsPage =
  | {
      activity: 'plan';
      page: JourneyPlannerDetailsPage;
    }
  | {
      activity: 'departures';
      stopAtcoCode: string;
    }
  | {
      activity: 'timetables';
      lineId: string;
    }
  | {
      activity: 'timetables';
      lineId: string;
      stopAtcoCode: string;
      stopName?: string | null;
    };

export type JourneyWidgetPage =
  | { kind: 'search' }
  | { kind: 'results' }
  | { kind: 'details'; details: JourneyWidgetDetailsPage };

export type JourneyWidgetPageState = PageStackState<
  WidgetTabId,
  JourneyWidgetPage
>;

export type JourneyWidgetPageAction = PageStackAction<
  WidgetTabId,
  JourneyWidgetPage
>;

const ACTIVITY_IDS: WidgetTabId[] = ['plan', 'departures', 'timetables'];

function buildDefaultSearchPage(_activityId: WidgetTabId): JourneyWidgetPage {
  return { kind: 'search' };
}

/**
 * Creates initial page state for all widget activities.
 */
export function createInitialJourneyWidgetPageState(
  initialActivity: WidgetTabId = 'plan',
): JourneyWidgetPageState {
  return createInitialPageStackState<WidgetTabId, JourneyWidgetPage>({
    activityIds: ACTIVITY_IDS,
    initialActivity,
    buildDefaultRootPage: buildDefaultSearchPage,
  });
}

/**
 * Applies a page-state action for the JourneyWidgetTabs domain.
 */
export function reduceJourneyWidgetPageState(
  state: JourneyWidgetPageState,
  action: JourneyWidgetPageAction,
): JourneyWidgetPageState {
  return reducePageStackState(state, action, buildDefaultSearchPage);
}

/**
 * Returns the current page for an activity, defaulting to active activity.
 */
export function getCurrentJourneyWidgetPage(
  state: JourneyWidgetPageState,
  activityId: WidgetTabId = state.activeActivity,
): JourneyWidgetPage {
  return getCurrentPage(state, activityId);
}

/**
 * Returns the full page stack for an activity, defaulting to active activity.
 */
export function getJourneyWidgetPageStack(
  state: JourneyWidgetPageState,
  activityId: WidgetTabId = state.activeActivity,
): JourneyWidgetPage[] {
  return getPageStack(state, activityId);
}

/**
 * Indicates whether the activity has a previous page to navigate back to.
 */
export function canGoBackInActivity(
  state: JourneyWidgetPageState,
  activityId: WidgetTabId = state.activeActivity,
): boolean {
  return canGoBack(state, activityId);
}

/**
 * Type guard for activity IDs used by the JourneyWidgetTabs domain.
 */
export function isJourneyWidgetActivityId(value: string): value is WidgetTabId {
  return ACTIVITY_IDS.includes(value as WidgetTabId);
}

export function createPlanItineraryDetailsPage(
  itineraryId: number,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'plan',
      page: {
        type: 'itinerary',
        itineraryId,
      },
    },
  };
}

export function createPlanStopDeparturesDetailsPage(
  options: PlanStopDeparturesDetailsPageOptions,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'plan',
      page: {
        type: 'stop-departures',
        itineraryId: options.itineraryId,
        stopAtcoCode: options.stopAtcoCode,
        stopName: options.stopName,
      },
    },
  };
}

export function createPlanLineTimetableDetailsPage(
  options: PlanLineTimetableDetailsPageOptions,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'plan',
      page: {
        type: 'line-timetable',
        itineraryId: options.itineraryId,
        lineId: options.lineId,
        initialLine: options.initialLine ?? null,
      },
    },
  };
}

export function createDeparturesStopDetailsPage(
  stopAtcoCode: string,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'departures',
      stopAtcoCode,
    },
  };
}

export function createTimetableLineDetailsPage(
  lineId: string,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'timetables',
      lineId,
    },
  };
}

export function createTimetableStopDeparturesDetailsPage(
  options: TimetableStopDeparturesDetailsPageOptions,
): JourneyWidgetPage {
  return {
    kind: 'details',
    details: {
      activity: 'timetables',
      lineId: options.lineId,
      stopAtcoCode: options.stopAtcoCode,
      stopName: options.stopName,
    },
  };
}

export function getJourneyPlannerDetailsPage(
  page: JourneyWidgetPage,
): JourneyPlannerDetailsPage | null {
  if (page.kind !== 'details' || page.details.activity !== 'plan') {
    return null;
  }

  return page.details.page;
}

export function getDeparturesStopAtcoCode(
  page: JourneyWidgetPage,
): string | null {
  if (page.kind !== 'details' || page.details.activity !== 'departures') {
    return null;
  }

  return page.details.stopAtcoCode;
}

export function getTimetableLineId(page: JourneyWidgetPage): string | null {
  if (page.kind !== 'details' || page.details.activity !== 'timetables') {
    return null;
  }

  return page.details.lineId;
}

export function getTimetableStopAtcoCode(
  page: JourneyWidgetPage,
): string | null {
  if (page.kind !== 'details' || page.details.activity !== 'timetables') {
    return null;
  }

  return 'stopAtcoCode' in page.details ? page.details.stopAtcoCode : null;
}

export function getTimetableStopName(page: JourneyWidgetPage): string | null {
  if (page.kind !== 'details' || page.details.activity !== 'timetables') {
    return null;
  }

  return 'stopName' in page.details ? (page.details.stopName ?? null) : null;
}
